import axios from "axios";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = "beno-admin-token";

export const adminApi = axios.create({ baseURL: API, headers: { "Content-Type": "application/json" } });

adminApi.interceptors.request.use((cfg) => {
  const t = localStorage.getItem(TOKEN_KEY);
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

adminApi.interceptors.response.use(
  (r) => r,
  (e) => {
    if (e.response?.status === 401) {
      localStorage.removeItem(TOKEN_KEY);
      if (typeof window !== "undefined" && !window.location.pathname.startsWith("/admin/login")) {
        window.location.href = "/admin/login";
      }
    }
    return Promise.reject(e);
  }
);

export const adminLogin = (email, password) =>
  adminApi.post("/admin/login", { email, password }).then((r) => {
    localStorage.setItem(TOKEN_KEY, r.data.token);
    return r.data;
  });

export const adminLogout = () => localStorage.removeItem(TOKEN_KEY);
export const isAdminAuthed = () => !!localStorage.getItem(TOKEN_KEY);

export const adminMe = () => adminApi.get("/admin/me").then((r) => r.data);
export const adminDashboard = () => adminApi.get("/admin/dashboard").then((r) => r.data);
export const adminIntelligence = (days = 14) => adminApi.get(`/admin/intelligence?days=${days}`).then((r) => r.data);
export const adminGins = () => adminApi.get("/admin/gins").then((r) => r.data);
export const adminUpdateGin = (id, body) => adminApi.put(`/admin/gins/${id}`, body).then((r) => r.data);
export const adminUpdateDna = (id, body) => adminApi.put(`/admin/gins/${id}/dna`, body).then((r) => r.data);
export const adminUploadImage = (id, file, sourceUrl, license) => {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("source_url", sourceUrl || "");
  fd.append("license_status", license || "internal");
  return adminApi.post(`/admin/gins/${id}/image`, fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
};
export const adminAlerts = () => adminApi.get("/admin/alerts").then((r) => r.data);
export const adminDismissAlert = (id) => adminApi.put(`/admin/alerts/${id}/dismiss`).then((r) => r.data);
export const adminRecommendations = () => adminApi.get("/admin/recommendations").then((r) => r.data);
export const adminMenuGaps = () => adminApi.get("/admin/menu-gaps").then((r) => r.data);
export const adminGetTonight = () => adminApi.get("/tonight").then((r) => r.data);
export const adminSetTonight = (body) => adminApi.put("/admin/tonight", body).then((r) => r.data);
export const adminGetScript = (id) => adminApi.get(`/admin/gins/${id}/script`).then((r) => r.data);
export const adminGenerateScript = (id) => adminApi.post(`/admin/gins/${id}/script/generate`).then((r) => r.data);
export const adminEditScript = (id, body) => adminApi.put(`/admin/gins/${id}/script`, body).then((r) => r.data);
export const adminCreateFlight = (body) => adminApi.post("/admin/flights", body).then((r) => r.data);
export const adminWeekly = () => adminApi.get("/admin/reports/weekly").then((r) => r.data);
export const adminWeeklyHeadline = () => adminApi.post("/admin/reports/weekly/headline").then((r) => r.data);

// Enrichment Scout
export const enrichSummary = (id) => adminApi.get(`/admin/enrichment/summary/${id}`).then((r) => r.data);
export const enrichScout = (body) => adminApi.post(`/admin/enrichment/scout`, body).then((r) => r.data);
export const enrichListSuggestions = (gin_id, status) => {
  const q = new URLSearchParams();
  if (gin_id) q.set("gin_id", gin_id);
  if (status) q.set("status", status);
  return adminApi.get(`/admin/enrichment/suggestions?${q}`).then((r) => r.data);
};
export const enrichActSuggestion = (id, body) => adminApi.put(`/admin/enrichment/suggestions/${id}`, body).then((r) => r.data);
export const enrichCreateImage = (body) => adminApi.post(`/admin/enrichment/image-candidates`, body).then((r) => r.data);
export const enrichActImage = (id, body) => adminApi.put(`/admin/enrichment/image-candidates/${id}`, body).then((r) => r.data);
export const enrichRenormalizeImage = (id) => adminApi.post(`/admin/enrichment/image-candidates/${id}/renormalize`).then((r) => r.data);

// Flavour map
export const enrichFlavourScout = (body) => adminApi.post(`/admin/enrichment/flavour-scout`, body).then((r) => r.data);
export const enrichActFlavour = (id, body) => adminApi.put(`/admin/enrichment/flavour-suggestions/${id}`, body).then((r) => r.data);

// Primary source
export const enrichSetPrimarySource = (gin_id, body) => adminApi.put(`/admin/enrichment/primary-source/${gin_id}`, body).then((r) => r.data);

// Bulk jobs
export const enrichStartJob = (body) => adminApi.post(`/admin/enrichment/jobs`, body).then((r) => r.data);
export const enrichListJobs = () => adminApi.get(`/admin/enrichment/jobs`).then((r) => r.data);
export const enrichGetJob = (id) => adminApi.get(`/admin/enrichment/jobs/${id}`).then((r) => r.data);
export const enrichCancelJob = (id) => adminApi.post(`/admin/enrichment/jobs/${id}/cancel`).then((r) => r.data);

// Source registry seed
export const enrichSeedSources = () => adminApi.post(`/admin/enrichment/sources/seed`).then((r) => r.data);

// Phase D — Editorial
export const enrichEditorialRewrite = (body) => adminApi.post(`/admin/enrichment/editorial-rewrite`, body).then((r) => r.data);
export const enrichActEditorial = (sid, body) => adminApi.put(`/admin/enrichment/editorial-suggestions/${sid}`, body).then((r) => r.data);
export const enrichWhyMatters = (body) => adminApi.post(`/admin/enrichment/why-it-matters`, body).then((r) => r.data);
export const enrichHistory = (gin_id) => adminApi.get(`/admin/enrichment/history?gin_id=${gin_id}`).then((r) => r.data);
export const enrichQueue = (filter_type) => {
  const q = filter_type ? `?filter_type=${filter_type}` : "";
  return adminApi.get(`/admin/enrichment/queue${q}`).then((r) => r.data);
};
export const enrichListDiscoveries = (include_archived = false) =>
  adminApi.get(`/admin/enrichment/discoveries?include_archived=${include_archived}`).then((r) => r.data);
export const enrichArchiveDiscovery = (id, is_archived) =>
  adminApi.put(`/admin/enrichment/discoveries/${id}`, { is_archived }).then((r) => r.data);
export const enrichCreateBenoDiscovery = (body) =>
  adminApi.post(`/admin/enrichment/discoveries/beno`, body).then((r) => r.data);
export const enrichListSources = () =>
  adminApi.get(`/admin/enrichment/sources`).then((r) => r.data);
export const enrichUpsertSource = (domain, body) =>
  adminApi.put(`/admin/enrichment/sources/${domain}`, body).then((r) => r.data);
export const enrichDeleteSource = (domain) =>
  adminApi.delete(`/admin/enrichment/sources/${domain}`).then((r) => r.data);

// ---------------- BENO Liquid Atlas — Menu Ingestion ----------------
export const menuUpload = (file) => {
  const fd = new FormData();
  fd.append("file", file);
  return adminApi.post(`/admin/menu/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
};
export const menuListBatches = () => adminApi.get(`/admin/menu/batches`).then((r) => r.data);
export const menuCapabilities = () => adminApi.get(`/admin/menu/capabilities`).then((r) => r.data);
export const menuGetBatch = (id) => adminApi.get(`/admin/menu/batches/${id}`).then((r) => r.data);
export const menuCanonicalize = (id) => adminApi.post(`/admin/menu/batches/${id}/canonicalize`).then((r) => r.data);
export const menuImport = (id, actions) => adminApi.post(`/admin/menu/batches/${id}/import`, { actions }).then((r) => r.data);
export const menuDeleteBatch = (id) => adminApi.delete(`/admin/menu/batches/${id}`).then((r) => r.data);

// Canonical beverages (admin)
export const beverageList = (params = {}) => {
  const qs = new URLSearchParams(params).toString();
  return adminApi.get(`/admin/beverages${qs ? `?${qs}` : ""}`).then((r) => r.data);
};
export const beverageGet = (id) => adminApi.get(`/admin/beverages/${id}`).then((r) => r.data);
export const beverageUpdate = (id, body) => adminApi.put(`/admin/beverages/${id}`, body).then((r) => r.data);
export const beveragePatch = (id, patch) => adminApi.patch(`/admin/beverages/${id}`, patch).then((r) => r.data);
export const beverageDelete = (id) => adminApi.delete(`/admin/beverages/${id}`).then((r) => r.data);
export const beverageSetAvailability = (id, status) =>
  adminApi.put(`/admin/beverages/${id}/availability`, { availability_status: status }).then((r) => r.data);
export const beverageBulk = (ids, action, value) =>
  adminApi.post(`/admin/beverages/bulk`, { ids, action, value }).then((r) => r.data);
export const beverageCategoriesPublic = () => axios.get(`${API}/beverages/categories`).then((r) => r.data);


// Public discoveries (no auth)
export const publicDiscoveries = (limit = 6) =>
  axios.get(`${API}/discoveries?limit=${limit}`).then((r) => r.data);

// Public events recorder (used by guest-facing pages)
export const recordEvent = (type, extra = {}) => {
  let sid = localStorage.getItem("beno-session");
  if (!sid) { sid = crypto.randomUUID(); localStorage.setItem("beno-session", sid); }
  return axios.post(`${API}/events`, { type, session_id: sid, ...extra }).catch(() => {});
};
